import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../contexts/AccountContext';
import syncService from '../services/syncService';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import './SyncPage.css';

// `hotelId` pins the list to one property instead of the navbar selection, and `embedded` drops the page
// header — together they let the hotel's Products tab show its own projects with this same list.
const SyncPage = ({ hotelId = null, embedded = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const navbar = useAccount();
  const { canCreateProducts } = navbar;
  const selectedHotel = hotelId || navbar.selectedHotel;
  const selectedAccount = hotelId ? null : navbar.selectedAccount;
  const selectedPartner = hotelId ? null : navbar.selectedPartner;

  const PAGE_SIZE = 20;

  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  useEffect(() => {
    loadProjects();
  }, [selectedAccount, selectedPartner, selectedHotel]);

  useEffect(() => {
    setCurrentPage(1);
    let filtered = projects;
    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.projectStatus?.toLowerCase() === statusFilter);
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.projectName?.toLowerCase().includes(q) ||
        p.templateName?.toLowerCase().includes(q) ||
        p.sourceSystemName?.toLowerCase().includes(q) ||
        p.targetSystemName?.toLowerCase().includes(q) ||
        p.apiKey?.toLowerCase().includes(q)
      );
    }
    setFilteredProjects(filtered);
  }, [searchTerm, statusFilter, projects]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await syncService.getSyncProjects(selectedAccount, selectedPartner, selectedHotel);
      setProjects(data || []);
      setFilteredProjects(data || []);
    } catch (err) {
      console.error('Error loading sync projects:', err);
      setError('Failed to load sync projects. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusDisplay = (status) => {
    if (!status) return status;
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  return (
    // Embedded in HotelForm's <form>: buttons are type="button" and Enter in the search box must not submit it.
    <div className={`sync-page${embedded ? ' sync-page-embedded' : ''}`}
         onKeyDown={(e) => { if (e.key === 'Enter' && e.target.tagName === 'INPUT') e.preventDefault(); }}>
      <div className="sync-container">
        {/* Header Section */}
        <div className="sync-header">
          <div>
            {!embedded && (
              <>
                <h1 className="sync-title">Sync Projects</h1>
                <p className="sync-subtitle">
                  Manage and monitor data synchronization projects
                </p>
              </>
            )}
          </div>
          <div className="header-actions">
            {/* Creating a project is an admin act ('A' on the property, its chain or its partner). A
                hotel 'U' still works every task on the projects that exist. The lambda enforces it. */}
            {canCreateProducts && (
              <Button
                variant="success"
                onClick={() => navigate(hotelId ? `/hotel/${hotelId}/sync/new` : '/sync/create')}
              >
                <Plus className="icon" />
                Create New Project
              </Button>
            )}
          </div>
        </div>

        {/* Search Bar */}
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search by project, template, system, or API key..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <div className="status-toggle">
            {['active', 'inactive', 'all'].map(s => (
              <button
                type="button"
                key={s}
                className={`status-toggle-btn${statusFilter === s ? ' active' : ''}`}
                onClick={() => setStatusFilter(s)}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="loading-container">
            <Spinner size="lg" />
            <p className="loading-text">Loading sync projects...</p>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="empty-state">
            <p>
              {searchTerm
                ? 'No sync projects match your search.'
                : 'No sync projects found for this account.'}
            </p>
          </div>
        ) : (() => {
          const totalPages = Math.ceil(filteredProjects.length / PAGE_SIZE);
          const pageProjects = filteredProjects.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
          // Group by chain when a partner is selected but no specific chain — gives the user
          // a chain-by-chain breakdown rather than a flat list (HP-58).
          const groupByChain = !!selectedPartner && !selectedAccount;
          const renderProjectCard = (project) => (
            <div key={project.syncuuid} className="project-card" onClick={() => navigate(`/sync/${project.syncId}`)}>
              <div className="project-card-content">
                <div className="project-main">
                  <div className="project-title-block">
                    <h3 className="project-name">{project.projectName}</h3>
                    <p className="project-template">{project.templateName}</p>
                    {project.hotelName && (
                      <p className="project-chain">Hotel: {project.hotelName}</p>
                    )}
                    {!groupByChain && project.accountName && (
                      <p className="project-chain">Chain: {project.accountName}</p>
                    )}
                    {project.apiKey && (
                      <p className="project-apikey" onClick={(e) => e.stopPropagation()} title="Connector API key">
                        <span style={{ color: '#6b7280', fontWeight: 600 }}>API Key: </span>
                        <code style={{ fontFamily: 'monospace', fontSize: '0.78rem', background: '#f3f4f6', padding: '0.1rem 0.35rem', borderRadius: '4px', userSelect: 'all' }}>{project.apiKey}</code>
                      </p>
                    )}
                  </div>
                  <div className="systems-row">
                    <div className="system-info">
                      {project.sourceSystemImage && (
                        <img src={project.sourceSystemImage} alt={project.sourceSystemName} className="system-logo" onError={e => { e.target.style.display = 'none'; }} />
                      )}
                      <div className="system-details">
                        <div className="system-label">Source</div>
                        <div className="system-name">{project.sourceSystemName}</div>
                      </div>
                    </div>
                    <div className="arrow-divider">→</div>
                    <div className="system-info">
                      {project.targetSystemImage && (
                        <img src={project.targetSystemImage} alt={project.targetSystemName} className="system-logo" onError={e => { e.target.style.display = 'none'; }} />
                      )}
                      <div className="system-details">
                        <div className="system-label">Target</div>
                        <div className="system-name">{project.targetSystemName}</div>
                      </div>
                    </div>
                  </div>
                  <span className={`status-badge ${getStatusColor(project.projectStatus)}`}>
                    {getStatusDisplay(project.projectStatus)}
                  </span>
                </div>
              </div>
            </div>
          );

          // Group page projects by chain (accountId/accountName). Stable order = chain name.
          const groupedByChain = (() => {
            if (!groupByChain) return null;
            const map = new Map();
            for (const p of pageProjects) {
              const key = p.accountId ?? '__unknown__';
              if (!map.has(key)) map.set(key, { accountId: p.accountId, accountName: p.accountName || 'Unknown chain', projects: [] });
              map.get(key).projects.push(p);
            }
            return Array.from(map.values()).sort((a, b) => String(a.accountName).localeCompare(String(b.accountName)));
          })();

          return (
            <>
              {groupByChain && groupedByChain ? (
                <div className="projects-by-chain">
                  {groupedByChain.map(group => (
                    <div key={group.accountId ?? 'unknown'} className="chain-group">
                      <h2 className="chain-group-title">
                        {group.accountName}
                        <span className="chain-group-count">{group.projects.length}</span>
                      </h2>
                      <div className="projects-list">
                        {group.projects.map(renderProjectCard)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="projects-list">
                  {pageProjects.map(renderProjectCard)}
                </div>
              )}
              {totalPages > 1 && (
                <div className="pagination">
                  <span className="pagination-info">
                    {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredProjects.length)} of {filteredProjects.length}
                  </span>
                  <div className="pagination-controls">
                    <button type="button" className="page-btn" onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>‹</button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                      .reduce((acc, p, idx, arr) => {
                        if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((p, idx) => p === '…'
                        ? <span key={`ellipsis-${idx}`} className="page-ellipsis">…</span>
                        : <button type="button" key={p} className={`page-btn${currentPage === p ? ' active' : ''}`} onClick={() => setCurrentPage(p)}>{p}</button>
                      )}
                    <button type="button" className="page-btn" onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}>›</button>
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
};

export default SyncPage;
